import { NzTreeNodeOptions } from "ng-zorro-antd/tree";

import { S3Item } from "../models/s3-item.model";

/** A folder the fold synthesised from the path segments of the keys it was given. */
interface S3FolderNode extends NzTreeNodeOptions {
  type: "folder";
  isLeaf: false;
  loaded: boolean;
  /** Left undefined until a real object contributes bytes below it. */
  size?: number;
}

export interface FoldOptions {
  /** Synthesised folders start expanded. */
  expanded?: boolean;
  /**
   * Whether a synthesised folder counts as fully listed. True when the keys came from an
   * exhaustive recursive listing; false when they are only a skeleton of keys already
   * known, so the folder still fetches its real contents the first time it is expanded.
   */
  markLoaded?: boolean;
}

/**
 * Folds a flat S3 listing into nz-tree node options.
 *
 * An S3 key already *is* its path, so no structure has to be inferred — only each key's
 * folder chain has to be found or created. That lookup goes through one flat Map keyed by
 * the accumulated prefix, which keeps it O(1) per segment. Searching a parent's `children`
 * for a matching segment instead would be O(siblings) per segment, degrading to O(n²) on
 * exactly the wide folders this exists to handle.
 *
 * Structure is derived from the shape of the keys alone, never from `item.type`, so the
 * result is correct whether or not the listing includes folder entries of its own.
 *
 * @param rootPrefix - the prefix every key sits under. It is the implicit root and is not
 *                     itself emitted; "" is the bucket root and needs no special casing.
 * @param items - the flat listing, in any order.
 * @returns the children of `rootPrefix`, ready to hand to `addChildren`.
 */
export function foldS3Keys(
  rootPrefix: string,
  items: readonly S3Item[],
  options: FoldOptions = {},
): NzTreeNodeOptions[] {
  const { expanded = false, markLoaded = true } = options;

  // Holds the very same objects that sit in the `children` arrays, so pushing a child or
  // adding to a size later is visible everywhere with no reconciliation pass.
  const folders = new Map<string, NzTreeNodeOptions>();
  const root: NzTreeNodeOptions = { title: "", key: rootPrefix, children: [] };
  folders.set(rootPrefix, root);

  for (const item of items) {
    const relative = item.key.slice(rootPrefix.length);
    if (!relative) continue; // the prefix's own marker object, describing the root itself

    const segments = relative.split("/");
    // Empty when the key ends in "/": a folder marker object, which contributes its folder
    // chain but no file of its own. Without this the tree grows blank-titled phantom files.
    const name = segments.pop()!;

    let parent = root;
    let prefix = rootPrefix;

    for (const segment of segments) {
      prefix += `${segment}/`;
      let folder = folders.get(prefix) as S3FolderNode | undefined;

      if (!folder) {
        folder = {
          title: segment,
          key: prefix,
          type: "folder",
          isLeaf: false,
          loaded: markLoaded,
          expanded,
          children: [],
        };
        folders.set(prefix, folder);
        parent.children!.push(folder);
      }

      // Only real objects carry bytes, so a folder entry can never double-count. A folder
      // with no file anywhere below it keeps `size` undefined, which the template renders
      // as nothing rather than a misleading "0 B".
      if (name) folder.size = (folder.size ?? 0) + (item.size ?? 0);

      parent = folder;
    }

    if (name) {
      // Spread first so storage_class, restore_state, available_until and hours_left all
      // reach the node template untouched.
      parent.children!.push({
        ...item,
        title: name,
        key: item.key,
        isLeaf: true,
        loaded: true,
      });
    }
  }

  return root.children!;
}
